import { Injectable } from '@angular/core';
import type { dateRangeDto, EchartsConfig, ILaneConfig, LaneConfig } from 'commons';
import { format } from 'date-fns';
import type { XAXisComponentOption } from 'echarts';

/**
 * Service responsible for building X-axis configurations for ECharts multilane charts.
 */
@Injectable({ providedIn: 'root' })
export class XAxisBuilderService {
  private readonly xAxisDateFormat = 'yyyy-MM-dd hh:mm';

  /**
   * Builds X-axis configurations for all lanes
   */
  buildXAxes(lanes: LaneConfig[] | ILaneConfig[], dateRange: dateRangeDto, config?: EchartsConfig): XAXisComponentOption[] {
    return lanes.map((laneConfig, index) => this.createXAxisComponent(laneConfig, index, dateRange, config));
  }

  /**
   * Updates X-axis boundaries based on series data and lane configuration
   */
  applyBoundaries(xAxis: XAXisComponentOption[], seriesData: number[], lanes: LaneConfig[] | ILaneConfig[], zoomSliderMode: string): XAXisComponentOption[] {
    return xAxis.map((axis, index) => {
      if (axis.type === 'value') {
        return this.applyValueAxisBoundaries(axis, seriesData);
      }
      return this.applyTimeAxisBoundaries(axis, lanes, index, zoomSliderMode);
    });
  }

  private createXAxisComponent(laneConfig: LaneConfig | ILaneConfig, gridIndex: number, dateRange: dateRangeDto, config?: EchartsConfig): XAXisComponentOption {
    const isValueAxis = laneConfig?.config?.type === 'value';

    return {
      type: laneConfig?.config?.type ?? 'time',
      show: true,
      axisPointer: {
        z: config?.xAxisPointerZ ?? 100,
        label: {
          formatter: (data: { value: number | string }) => this.formatAxisPointerLabel(data.value, laneConfig),
        },
      },
      axisLine: {
        show: config?.xAxisShowLine ?? true,
        onZero: false,
        lineStyle: {
          color: config?.xAxisLineColor ?? '#333',
          width: config?.xAxisLineWidth ?? 1,
        },
      },
      axisTick: {
        show: config?.xAxisShowTicks ?? true,
        length: config?.xAxisTickLength ?? 5,
      },
      axisLabel: {
        show: config?.xAxisShowLabels ?? true,
        showMinLabel: true,
        showMaxLabel: true,
        fontSize: config?.xAxisLabelFontSize ?? 12,
        color: config?.xAxisLabelColor ?? '#333',
        rotate: config?.xAxisLabelRotate ?? 0,
      },
      splitLine: {
        show: config?.xAxisShowSplitLine ?? true,
        lineStyle: {
          color: config?.xAxisSplitLineColor ?? '#eee',
          type: config?.xAxisSplitLineType ?? 'solid',
        },
      },
      min: isValueAxis ? undefined : new Date(dateRange.start_date),
      max: isValueAxis ? undefined : new Date(dateRange.end_date),
      id: `LaneX-${gridIndex}`,
      gridIndex,
      interval: isValueAxis ? 'auto' : 1,
      nameLocation: 'middle',
      nameGap: config?.xAxisNameGap ?? 25,
      name: this.getAxisName(laneConfig, isValueAxis),
    } as XAXisComponentOption;
  }

  private formatAxisPointerLabel(value: number | string, laneConfig: LaneConfig | ILaneConfig): string {
    if (laneConfig?.config?.type === 'time') {
      return format(Math.floor(Number(value)), this.xAxisDateFormat);
    }
    return String(value);
  }

  private getAxisName(laneConfig: LaneConfig | ILaneConfig, isValueAxis: boolean): string {
    if (isValueAxis) {
      return laneConfig?.config?.xAxisField?.label ?? '';
    }
    return 'datetime';
  }

  private applyValueAxisBoundaries(axis: XAXisComponentOption, seriesData: number[]): XAXisComponentOption {
    if (seriesData.length === 0) {
      return axis;
    }

    return {
      ...axis,
      min: Math.min(...seriesData),
      max: Math.max(...seriesData),
    };
  }

  private applyTimeAxisBoundaries(
    axis: XAXisComponentOption,
    lanes: LaneConfig[] | ILaneConfig[],
    index: number,
    zoomSliderMode: string
  ): XAXisComponentOption {
    const lane = lanes[index];
    const isLaneMode = zoomSliderMode === 'lane';

    if (isLaneMode && lane) {
      return {
        ...axis,
        min: lane.firstControlTimestamp ? new Date(lane.firstControlTimestamp) : axis.min,
        max: lane.lastControlTimestamp ? new Date(lane.lastControlTimestamp) : axis.max,
      };
    }

    const timestamps = this.collectAllTimestamps(lanes);

    return {
      ...axis,
      min: timestamps.min.length > 0 ? Math.min(...timestamps.min) : axis.min,
      max: timestamps.max.length > 0 ? Math.max(...timestamps.max) : axis.max,
    };
  }

  private collectAllTimestamps(lanes: (LaneConfig | ILaneConfig)[]): {
    min: number[];
    max: number[];
  } {
    return {
      min: lanes.filter((l) => l.firstControlTimestamp).map((l) => new Date(l.firstControlTimestamp!).getTime()),
      max: lanes.filter((l) => l.lastControlTimestamp).map((l) => new Date(l.lastControlTimestamp!).getTime()),
    };
  }
}
